import React, { useState, useEffect, useCallback } from "react";
import { getMonthly, downloadMyReport } from "../../../services/attendanceApi";
import Icons from "./Icons";

/**
 * MonthlyStats — monthly summary as an SVG donut chart.
 * Segments: Present / Late / Absent (+ Half-day). Clicking a segment or a
 * legend row opens a mini popup with that segment's exact count and share.
 * Hours / Overtime / Efficiency shown as a compact footer row.
 * Pure SVG — no charting library.
 */

const SEG = [
  { key: "present", label: "Present", color: "#12805C" },
  { key: "late", label: "Late", color: "#B45309" },
  { key: "absent", label: "Absent", color: "#C0334E" },
  { key: "halfDay", label: "Half-day", color: "#2ABACC" },
  { key: "onLeave", label: "On Leave", color: "#6366F1" },
  { key: "wfh", label: "WFH", color: "#0EA5E9" },
];

function MonthlyStats({ refreshKey, month, year }) {
  const now = new Date();
  const m = month || now.getMonth() + 1;
  const y = year || now.getFullYear();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [popup, setPopup] = useState(null); // { label, count, pct, color }
  const [downloading, setDownloading] = useState("");  // "" | "pdf" | "excel"
  const [dlError, setDlError] = useState("");

  // Employee downloads their OWN monthly attendance report (PDF / Excel).
  // Uses downloadMyReport() -> GET /report/my (binary blob, auth attached).
  const handleDownload = async (format) => {
    if (downloading) return;
    try {
      setDlError("");
      setDownloading(format);
      await downloadMyReport(format, m, y);
    } catch (err) {
      setDlError(err.message || "Could not download report.");
    } finally {
      setDownloading("");
    }
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getMonthly(m, y);
      const data = res && res.data ? res.data : res;

      if (data && data.summary) {
        setStats(normalize(data.summary));
      } else {
        const list = Array.isArray(data) ? data : (data.records || data.days || []);
        const s = { present: 0, late: 0, absent: 0, halfDay: 0, onLeave: 0, wfh: 0, totalHours: 0, overtime: 0, effSum: 0, effCount: 0 };
        list.forEach((r) => {
          const st = (r.status || "").toUpperCase();
          const isWfh = !!(r.is_wfh ?? r.isWfh);
          // Count each recorded day exactly once by status. A WFH-flagged
          // PRESENT/LATE day still counts toward WFH so leave/WFH-heavy
          // months are not reported as "No attendance recorded".
          if (st === "WFH") s.wfh++;
          else if (st === "ON_LEAVE") s.onLeave++;
          else if (isWfh && (st === "PRESENT" || st === "LATE")) s.wfh++;
          else if (st === "PRESENT") s.present++;
          else if (st === "LATE") s.late++;
          else if (st === "ABSENT") s.absent++;
          else if (st === "HALF_DAY") s.halfDay++;
          s.totalHours += Number(r.totalHours || r.total_hours || 0);
          s.overtime += Number(r.overtimeHours || r.overtime_hours || 0);
          const eff = Number(r.efficiency || 0);
          if (eff > 0) { s.effSum += eff; s.effCount++; }
        });
        s.efficiency = s.effCount ? (s.effSum / s.effCount).toFixed(1) : 0;
        setStats(s);
      }
    } catch (err) {
      setError(err.message || "Could not load stats.");
    } finally {
      setLoading(false);
    }
  }, [m, y]); // eslint-disable-line

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return <div className="atl-card atl-stats"><div className="atl-skeleton atl-skeleton--tall" /></div>;
  }

  const s = stats || {};
  const segData = SEG.map((seg) => ({ ...seg, count: Number(s[seg.key] || 0) }))
    .filter((seg) => seg.count > 0);
  const total = segData.reduce((a, b) => a + b.count, 0);

  // Build donut arcs (stroke-dasharray on a circle)
  const R = 54, C = 2 * Math.PI * R;
  let offset = 0;
  const arcs = segData.map((seg) => {
    const frac = total ? seg.count / total : 0;
    const len = frac * C;
    const arc = { ...seg, dash: `${len} ${C - len}`, off: -offset, pct: Math.round(frac * 100) };
    offset += len;
    return arc;
  });

  const openSeg = (seg) => setPopup({ label: seg.label, count: seg.count, pct: seg.pct ?? Math.round((seg.count / (total || 1)) * 100), color: seg.color });

  return (
    <div className="atl-card atl-stats">
      <div className="atl-stats__head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}><span className="atl-h3ic">{Icons.stats}</span>{(m === now.getMonth() + 1 && y === now.getFullYear()) ? "This Month" : new Date(y, m - 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</h3>
        <div className="atl-report-dl" style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            className="atl-btn atl-btn--sm"
            onClick={() => handleDownload("pdf")}
            disabled={!!downloading}
            title="Download my attendance report as PDF"
          >
            <span className="atl-h3ic">{Icons.download}</span>{downloading === "pdf" ? "..." : "PDF"}
          </button>
          <button
            type="button"
            className="atl-btn atl-btn--sm"
            onClick={() => handleDownload("excel")}
            disabled={!!downloading}
            title="Download my attendance report as Excel"
          >
            <span className="atl-h3ic">{Icons.download}</span>{downloading === "excel" ? "..." : "Excel"}
          </button>
        </div>
      </div>
      {dlError && <div className="atl-error">{dlError}</div>}
      {error && <div className="atl-error">{error}</div>}

      {total === 0 ? (
        <EmptyDonut />
      ) : (
        <div className="atl-donut-wrap">
          {/* Donut */}
          <div className="atl-donut">
            <svg viewBox="0 0 140 140" width="150" height="150">
              <circle cx="70" cy="70" r={R} fill="none" stroke="#EEF2F7" strokeWidth="16" />
              {arcs.map((a) => (
                <circle
                  key={a.key}
                  cx="70" cy="70" r={R} fill="none"
                  stroke={a.color} strokeWidth="16" strokeLinecap="butt"
                  strokeDasharray={a.dash} strokeDashoffset={a.off}
                  transform="rotate(-90 70 70)"
                  className="atl-donut__seg"
                  onClick={() => openSeg(a)}
                />
              ))}
              <text x="70" y="65" textAnchor="middle" className="atl-donut__total">{total}</text>
              <text x="70" y="84" textAnchor="middle" className="atl-donut__totlabel">days</text>
            </svg>
          </div>

          {/* Clickable legend */}
          <div className="atl-donut-legend">
            {arcs.map((a) => (
              <button key={a.key} className="atl-donut-legend__row" onClick={() => openSeg(a)}>
                <span className="atl-donut-legend__dot" style={{ background: a.color }} />
                <span className="atl-donut-legend__label">{a.label}</span>
                <span className="atl-donut-legend__val">{a.count} · {a.pct}%</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Compact metrics footer */}
      <div className="atl-metrics">
        <div className="atl-metric"><span className="atl-metric__val">{fmtNum(s.totalHours)}</span><span className="atl-metric__lbl">Total Hrs</span></div>
        <div className="atl-metric"><span className="atl-metric__val">{fmtNum(s.overtime)}</span><span className="atl-metric__lbl">Overtime</span></div>
        <div className="atl-metric"><span className="atl-metric__val">{(s.efficiency ?? 0)}%</span><span className="atl-metric__lbl">Efficiency</span></div>
      </div>

      {/* Mini popup on segment/legend click */}
      {popup && (
        <div className="atl-popup" onClick={() => setPopup(null)}>
          <div className="atl-popup__box atl-popup__box--mini" onClick={(e) => e.stopPropagation()}>
            <div className="atl-popup__head">
              <strong><span className="atl-mini-dot" style={{ background: popup.color }} />{popup.label}</strong>
              <button onClick={() => setPopup(null)} aria-label="Close">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>
            <div className="atl-mini-big" style={{ color: popup.color }}>{popup.count}</div>
            <div className="atl-mini-sub">{popup.label} days this month · {popup.pct}% of recorded days</div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyDonut() {
  return (
    <div className="atl-empty-state">
      <svg viewBox="0 0 64 64" width="72" height="72" fill="none" stroke="#C3CCD8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="32" cy="32" r="20" />
        <path d="M32 12a20 20 0 0 1 20 20" stroke="#2ABACC" />
      </svg>
      <p>No attendance recorded yet this month.</p>
    </div>
  );
}

function normalize(sum) {
  return {
    present: sum.present ?? sum.present_days ?? 0,
    late: sum.late ?? sum.late_days ?? 0,
    absent: sum.absent ?? sum.absent_days ?? 0,
    halfDay: sum.halfDay ?? sum.half_day ?? sum.half_day_days ?? 0,
    onLeave: sum.onLeave ?? sum.on_leave ?? sum.on_leave_days ?? sum.leave_days ?? 0,
    wfh: sum.wfh ?? sum.wfh_days ?? sum.wfhDays ?? 0,
    totalHours: sum.totalHours ?? sum.total_hours ?? 0,
    overtime: sum.overtime ?? sum.overtime_hours ?? 0,
    efficiency: sum.efficiency ?? 0,
  };
}

function fmtNum(n) {
  const v = Number(n || 0);
  return Number.isInteger(v) ? v : v.toFixed(1);
}

export default MonthlyStats;
